import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Text,
} from "@chakra-ui/react";
import { useCallback, useEffect, useRef, useState } from "react";
import AppButton from "@/features/line-sheet-sets/app-button";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import {
  OrderRuleDraftResponse,
  useGenerateOrderRuleDraft,
  useGetOrderRuleDraft,
} from "@/features/line-sheet-sets/hooks/use-order-rule-draft";

/** 서버의 normalize_rule_text 와 같은 규칙 — 줄마다 공백을 눌러 앞뒤 빈 줄을 버린다. */
function normalizeRuleText(value: string | null | undefined): string {
  return (value ?? "")
    .split("\n")
    .map((line) => line.trim().replace(/\s+/g, " "))
    .join("\n")
    .trim();
}

interface OrderRuleDraftBoxProps {
  lineSheetSetId: number;
  /** 폼이 수정 모드면 초안을 오더룰 칸에 넣기만 하고, 저장은 폼 저장 버튼이 맡는다. */
  isEditing: boolean;
  onApply: (draft: string) => void;
  /**
   * 수정 모드가 아니어도 초안을 바로 저장할 수 있게 하는 경로.
   * 넘어오면 '초안 적용'이 확인 모달을 띄우고, 확인 시 이걸 호출한다.
   */
  onSave?: (draft: string) => void;
  isSaving?: boolean;
  /** 지금 저장돼 있는 오더룰 — 초안과 같으면 더 적용할 것이 없어 버튼을 잠근다. */
  appliedText?: string;
}

/**
 * 오더룰 원문과 AI 초안을 나란히 보여주고, 초안을 오더룰 칸에 넣어주는 상자.
 *
 * 초안을 그대로 쓸지 고쳐 쓸지는 사람이 정한다 — 이 컴포넌트는 오더룰을 저장하지 않는다.
 * 저장은 기존 경로 그대로이고, 저장 시점에 서버가 초안과 대조해 '그대로 승인'인지
 * '사람이 고침'인지 기록한다. 고친 건이 다음 초안의 교정 예시가 된다.
 *
 * AI 가 틀렸을 때 고치는 자리는 오더룰 칸 그 자체다. 초안을 적용해 두고 오더룰을 고쳐
 * 저장하면 서버가 초안과 달라진 것을 보고 EDITED(= AI 가 틀림)로 다시 판정한다.
 * 생성·확정은 서버가 이력 테이블에 한 줄씩 쌓는다.
 */
export default function OrderRuleDraftBox({
  lineSheetSetId,
  isEditing,
  onApply,
  onSave,
  isSaving = false,
  appliedText,
}: OrderRuleDraftBoxProps) {
  const { t, tTitle } = useI18nHelper();
  const { error: showError } = useAppToasts();
  const [draft, setDraft] = useState<OrderRuleDraftResponse | null>(null);
  const [isSourceOpen, setIsSourceOpen] = useState(false);
  // 적용 확인 모달 — 저장될 내용을 눈으로 한 번 확인하고 넘긴다.
  const [applyingText, setApplyingText] = useState<string | null>(null);

  const {
    fire: fireGet,
    data: getData,
    isLoading: isGetLoading,
    clear: clearGet,
  } = useGetOrderRuleDraft();

  const {
    fire: fireGenerate,
    data: generateData,
    isLoading: isGenerateLoading,
    error: generateError,
    clear: clearGenerate,
  } = useGenerateOrderRuleDraft();

  useEffect(() => {
    if (lineSheetSetId > -1) {
      fireGet({ lineSheetSetId }, {});
    }
  }, [lineSheetSetId, fireGet]);

  useEffect(() => {
    if (getData) {
      setDraft(getData);
      clearGet();
    }
  }, [getData, clearGet]);

  useEffect(() => {
    if (generateData) {
      setDraft(generateData);
      clearGenerate();
    }
  }, [generateData, clearGenerate]);

  useEffect(() => {
    showError(generateError);
  }, [showError, generateError]);

  // 저장이 끝나면 서버가 초안 상태를 다시 판정하므로(ACCEPTED/EDITED) 그 결과를 다시 읽는다.
  const wasSavingRef = useRef(false);
  useEffect(() => {
    if (wasSavingRef.current && !isSaving && lineSheetSetId > -1) {
      fireGet({ lineSheetSetId }, {});
    }
    wasSavingRef.current = isSaving;
  }, [isSaving, lineSheetSetId, fireGet]);

  const handleGenerate = useCallback(() => {
    fireGenerate({ lineSheetSetId }, {});
  }, [fireGenerate, lineSheetSetId]);

  const isLoading = isGetLoading || isGenerateLoading;
  const status = draft?.status ?? "NONE";
  const draftText = (draft?.draft ?? "").trim();
  // 저장된 오더룰이 이미 초안과 같으면 적용할 것이 없다.
  // 서버와 같은 규칙(줄 단위 trim + 앞뒤 빈 줄 제거)으로 견줘야 공백 차이로 어긋나지 않는다.
  const isAlreadyApplied =
    draftText !== "" && normalizeRuleText(appliedText) === draftText;
  // 메일 원문은 빈 줄이 잔뜩 섞여 와서 그대로 두면 스크롤만 길어진다 — 빈 줄은 한 줄까지만.
  const sourceText = (draft?.sourceText ?? "")
    .replace(/(?:[ \t]*\n){3,}/g, "\n\n")
    .trim();
  const hasAnything = draftText !== "" || sourceText !== "";

  // 초안도 원문도 없고 아직 생성해 본 적도 없으면 자리만 차지하므로 생성 버튼만 둔다.
  if (!hasAnything && status === "NONE" && !isLoading) {
    return (
      <Flex alignItems={"center"} gap={"8px"} margin={"4px 0"}>
        <Text fontSize={"11px"} color={"#8F8F8C"}>
          {t("order_rule_draft_none")}
        </Text>
        <AppButton
          variant={"normal"}
          size={"small"}
          onClick={handleGenerate}
          isLoading={isGenerateLoading}
        >
          <Text textStyle={"body-sm"}>
            {tTitle("generate_order_rule_draft")}
          </Text>
        </AppButton>
      </Flex>
    );
  }

  return (
    <Box
      margin={"4px 0 8px 0"}
      borderRadius={"6px"}
      border={"0.5px solid #C9D8E5"}
      background={"#F5F9FC"}
      overflow={"hidden"}
    >
      <Flex alignItems={"center"} gap={"8px"} padding={"8px 10px"}>
        <Text fontSize={"12px"} fontWeight={"700"} color={"#2C5C86"}>
          {tTitle("order_rule_draft")}
        </Text>
        {status === "ACCEPTED" && (
          <Text fontSize={"11px"} color={"#3B7A4B"}>
            {t("order_rule_draft_accepted")}
          </Text>
        )}
        {status === "EDITED" && (
          <Text fontSize={"11px"} color={"#8A6A00"}>
            {t("order_rule_draft_edited")}
          </Text>
        )}
        <Spacer />
        {sourceText !== "" && (
          <AppButton
            variant={"normal"}
            size={"small"}
            onClick={() => setIsSourceOpen((prev) => !prev)}
          >
            <Text textStyle={"body-sm"}>
              {isSourceOpen
                ? tTitle("hide_order_rule_source")
                : tTitle("show_order_rule_source")}
            </Text>
          </AppButton>
        )}
        <AppButton
          variant={"primary"}
          size={"small"}
          isDisabled={
            draftText === "" ||
            isLoading ||
            isAlreadyApplied ||
            (!onSave && !isEditing)
          }
          onClick={() => {
            // 저장 경로가 있으면 무엇이 저장되는지 보여주고 확인을 받는다.
            if (onSave) {
              setApplyingText(draftText);
              return;
            }
            onApply(draftText);
          }}
        >
          <Text textStyle={"body-sm-bold"}>
            {tTitle("apply_order_rule_draft")}
          </Text>
        </AppButton>
      </Flex>

      {draft?.error && (
        <Text
          fontSize={"11px"}
          color={"#A94442"}
          padding={"0 10px 8px 10px"}
          whiteSpace={"pre-wrap"}
        >
          {draft.error}
        </Text>
      )}

      {draftText !== "" ? (
        <Box
          padding={"8px 10px"}
          background={"#FFF"}
          borderTop={"0.5px solid #C9D8E5"}
        >
          <Text
            fontSize={"12px"}
            lineHeight={1.6}
            color={"#444444"}
            whiteSpace={"pre-wrap"}
          >
            {draftText}
          </Text>
        </Box>
      ) : (
        !draft?.error &&
        !isLoading && (
          <Text fontSize={"11px"} color={"#8F8F8C"} padding={"0 10px 8px 10px"}>
            {t("order_rule_draft_empty")}
          </Text>
        )
      )}

      <Modal
        isOpen={applyingText !== null}
        onClose={() => setApplyingText(null)}
        isCentered={true}
        size={"lg"}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Text textStyle={"body-md-bold"}>
              {tTitle("apply_order_rule_draft")}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize={"12px"} color={"#6F6F6C"} marginBottom={"8px"}>
              {t("order_rule_draft_apply_confirm")}
            </Text>
            <Box
              padding={"8px 10px"}
              borderRadius={"4px"}
              border={"0.5px solid #C9D8E5"}
              background={"#FAFAF8"}
              maxHeight={"320px"}
              overflowY={"auto"}
            >
              <Text
                fontSize={"12px"}
                lineHeight={1.6}
                color={"#444444"}
                whiteSpace={"pre-wrap"}
              >
                {applyingText}
              </Text>
            </Box>
          </ModalBody>
          <ModalFooter gap={"8px"}>
            <AppButton
              variant={"normal"}
              size={"small"}
              onClick={() => setApplyingText(null)}
            >
              <Text textStyle={"body-sm"}>{tTitle("cancel")}</Text>
            </AppButton>
            <AppButton
              variant={"primary"}
              size={"small"}
              isLoading={isSaving}
              onClick={() => {
                if (applyingText === null) return;
                onSave?.(applyingText);
                setApplyingText(null);
              }}
            >
              <Text textStyle={"body-sm-bold"}>{tTitle("confirm")}</Text>
            </AppButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {isSourceOpen && sourceText !== "" && (
        <Box
          padding={"8px 10px"}
          background={"#FAFAF8"}
          borderTop={"0.5px solid #C9D8E5"}
          maxHeight={"320px"}
          overflowY={"auto"}
        >
          <Text
            fontSize={"11px"}
            fontWeight={"700"}
            color={"#6F6F6C"}
            marginBottom={"4px"}
          >
            {tTitle("order_rule_source")}
          </Text>
          <Text
            fontSize={"11px"}
            lineHeight={1.6}
            color={"#6F6F6C"}
            whiteSpace={"pre-wrap"}
          >
            {sourceText}
          </Text>
        </Box>
      )}
    </Box>
  );
}
